import React, { useState } from 'react';
import Papa from 'papaparse';
import {
  TextField,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Modal,
  Backdrop,
  Fade,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CsvUploader from '../ex_product_component/CsvUploader.jsx';

const ProductFilter = ({
  searchTerm,
  setSearchTerm,
  filters,
  setFilters,
}) => {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuCloseLocal = () => {
    setMenuAnchorEl(null);
  };

  const fetchAndDownloadCSV = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/get-exhibitors-products-without-pagination');
      const json = await res.json();
      const { items } = json.data;

      const csvData = [];

      items.forEach(product => {
        csvData.push({
          type: 'product',
          woocommerce_product_id: product.woocommerce_product_id ?? '',
          variation_id: '',
          event_name: product.event_name ?? '',
          price: product.price ?? '',
          stock: product.total_exhibitor ?? '',
          updated_price: '',
          updated_stock: '',
        });

        product.variants?.forEach(variant => {
          csvData.push({
            type: 'variant',
            woocommerce_product_id: variant.product_id ?? '',
            variation_id: variant.product_variant_id ?? '',
            event_name: variant.name ?? '',
            price: variant.price ?? '',
            stock: variant.stock ?? '',
            updated_price: '',
            updated_stock: '',
          });
        });
      });

      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'products-and-variants.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('CSV download error:', err);
    } finally {
      setLoading(false);
      handleMenuCloseLocal();
    }
  };

  const handleUploadClick = () => {
    setUploadModalOpen(true);
    handleMenuCloseLocal();
  };

  const handleUploadModalClose = () => {
    setUploadModalOpen(false);
  };

  return (
    <Box
      sx={{
        width: '80%',
        margin: '0 auto',
        padding: '16px 24px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow:
          '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        marginBottom: '16px',
        '&:hover': {
          boxShadow:
            '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -4px rgba(0, 0, 0, 0.2)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Grid
        container
        spacing={2}
        alignItems="center"
        wrap="wrap"
        sx={{
          [theme.breakpoints.up('sm')]: {
            flexWrap: 'nowrap',
          },
        }}
      >
        <Grid item xs={12} sm={3} md={5}>
          <TextField
            label="Search by product name"
            variant="outlined"
            size="small"
            name="searchTerm"
            value={searchTerm}
            onChange={handleSearchChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            label="Location"
            variant="outlined"
            size="small"
            name="location"
            value={filters.location}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={3} md={4}>
          <TextField
            select
            label="Product Type"
            variant="outlined"
            size="small"
            name="product_type"
            value={filters.product_type || ''}
            onChange={handleInputChange}
            fullWidth
            SelectProps={{ native: true }}
          >
            <option value="">All Types</option>
            <option value="event">🎫 Events</option>
            <option value="attendees">👥 Attendees</option>
            <option value="credit">💳 Credit Plans</option>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={3} md={4}>
          <TextField
            label="Total Exhibitors"
            variant="outlined"
            size="small"
            type="number"
            name="total_exhibitor"
            value={filters.total_exhibitor}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm="auto"
          md="auto"
          sx={{
            display: 'flex',
            justifyContent: {
              xs: 'flex-start',
              sm: 'flex-end',
            },
            alignItems: 'center',
            flexGrow: 1,
          }}
        >
          <IconButton
            aria-label="more"
            aria-controls="long-menu"
            aria-haspopup="true"
            onClick={handleMenuOpen}
          >
            <MoreVertIcon />
          </IconButton>
        </Grid>
      </Grid>

      {/* MENU */}
      <Menu
        id="long-menu"
        anchorEl={menuAnchorEl}
        keepMounted
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuCloseLocal}
        PaperProps={{ style: { width: '27ch' } }}
      >
        <MenuItem onClick={() => { setDownloadModalOpen(true); handleMenuCloseLocal(); }} disabled={loading}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          Download Products CSV
        </MenuItem>
        <MenuItem onClick={handleUploadClick}>
          <UploadFileIcon fontSize="small" sx={{ mr: 1 }} />
          Upload Product CSV
        </MenuItem>
      </Menu>

      {/* UPLOAD MODAL */}
      <Modal
        aria-labelledby="upload-csv-modal-title"
        open={uploadModalOpen}
        onClose={handleUploadModalClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Fade in={uploadModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 420,
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography id="upload-csv-modal-title" variant="h6" mb={2}>
              Upload Product CSV
            </Typography>
            <CsvUploader />
          </Box>
        </Fade>
      </Modal>

      {/* DOWNLOAD MODAL */}
      <Modal
        aria-labelledby="download-csv-modal-title"
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Fade in={downloadModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 420,
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography id="download-csv-modal-title" variant="h6" mb={2}>
              Download Products CSV
            </Typography>
            <Button
              variant="contained"
              onClick={fetchAndDownloadCSV}
              disabled={loading}
              size="small"
              sx={{ fontSize: '0.75rem', width: 'fit-content' }}
            >
              {loading ? 'Generating CSV...' : 'Download CSV'}
            </Button>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default ProductFilter;
